import React, { useState } from "react";
import { X, Loader2, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Fase 12 · Módulo B — Post-live-session NPS survey.
 *
 * Renders 0–10 score buttons (color-coded by class), optional comment,
 * and (only when score ≥ 8) a checkbox to consent auto-publishing as review.
 *
 * Props:
 *   - sessionId · string
 *   - sessionTitle · string (optional)
 *   - onDone(response) · callback with created NPS response
 *   - onClose() · dismiss without submit
 */
export default function NpsModal({ sessionId, sessionTitle, onDone, onClose }) {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const isPromoter = score !== null && score >= 8;
  const classLabel = score === null ? "" :
    score >= 9 ? "Promotor" : score >= 7 ? "Neutro" : "Detractor";
  const classColor = score === null ? "" :
    score >= 9 ? "text-emerald-300" : score >= 7 ? "text-amber-300" : "text-red-300";

  const submit = async () => {
    if (score === null) {
      toast.error("Selecciona un puntaje del 0 al 10.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/live/${sessionId}/nps`, {
        score, comment: comment.trim(),
        consent_to_publish_as_review: isPromoter && consent,
      });
      toast.success("¡Gracias por tu feedback!");
      onDone && onDone(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="nps-modal">
      <div className="relative w-full max-w-lg rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} data-testid="nps-close-btn"
                className="absolute top-3 right-3 text-gray-400 hover:text-white p-1">
          <X className="w-4 h-4" />
        </button>
        <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-bold">DCC · NPS</p>
        <h2 className="mt-1 text-2xl font-bold text-white font-display">¿Cómo estuvo la sesión?</h2>
        {sessionTitle && (
          <p className="mt-1 text-xs text-gray-400 truncate">{sessionTitle}</p>
        )}
        <p className="mt-4 text-sm text-gray-300">
          ¿Qué tan probable es que recomiendes esta sesión a un colega? (0 = muy poco · 10 = totalmente)
        </p>
        <div className="mt-4 grid grid-cols-11 gap-1" data-testid="nps-score-grid">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
            const active = score === n;
            const color = n >= 9 ? "bg-emerald-500 text-[#0B0F14]" :
                          n >= 7 ? "bg-amber-500 text-[#0B0F14]" :
                          "bg-red-500 text-white";
            return (
              <button key={n} onClick={() => setScore(n)}
                      data-testid={`nps-score-${n}`}
                      className={`h-10 rounded-md text-sm font-bold border transition ${
                        active ? `${color} border-transparent` : "bg-white/5 border-white/10 text-white hover:bg-white/10"
                      }`}>
                {n}
              </button>
            );
          })}
        </div>
        {score !== null && (
          <p className={`mt-2 text-[10px] uppercase tracking-widest font-bold ${classColor}`}>
            {classLabel}
          </p>
        )}
        <label className="block mt-4">
          <span className="block text-[11px] uppercase tracking-widest text-gray-400 font-semibold mb-1">
            Cuéntanos por qué (opcional)
          </span>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                    data-testid="nps-comment-input"
                    rows={3} maxLength={800}
                    className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#F26A21]"
                    placeholder="Ej. Excelente ejemplo aplicado a mi banco…" />
          <span className="text-[10px] text-gray-500">{comment.length}/800</span>
        </label>
        {isPromoter && (
          <label className="mt-3 inline-flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                   data-testid="nps-consent-checkbox"
                   className="mt-0.5 accent-[#F26A21]" />
            <span>
              <Star className="w-3 h-3 inline text-[#F26A21] mr-1" />
              Autorizo publicar mi comentario como reseña del programa en el catálogo público (previa moderación).
            </span>
          </label>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} data-testid="nps-skip-btn"
                  className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">
            Saltar
          </button>
          <button onClick={submit} disabled={sending || score === null}
                  data-testid="nps-submit-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            Enviar
          </button>
        </div>
      </div>
    </div>
  );
}
